/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Beta\BetaWelcome.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * Message de bienvenue pour les bêta-testeurs
 * Affiché sur le Dashboard lors de la première visite
 */
import React, { useState, useEffect } from 'react';

interface BetaWelcomeProps {
  onClose?: () => void;
}

const BetaWelcome: React.FC<BetaWelcomeProps> = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà vu le message
    const hasSeenWelcome = localStorage.getItem('beta_welcome_seen');
    if (!hasSeenWelcome) {
      setIsVisible(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem('beta_welcome_seen', 'true');
    setIsVisible(false);
    if (onClose) onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg shadow-lg p-6 mb-6">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center mb-3">
            <span className="text-4xl mr-3">🚀</span>
            <h2 className="text-2xl font-bold text-gray-900">
              Bienvenue, Bêta-Testeur !
            </h2>
          </div>
          
          <p className="text-gray-700 mb-4">
            Merci de participer à l'amélioration de <strong>SelleriePro</strong>. 
            Votre feedback est essentiel pour perfectionner l'application !
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Ce que vous pouvez faire */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-green-700 mb-2 flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Ce que vous pouvez faire
              </h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✅ Créer des devis complets</li>
                <li>✅ Tester le calepinage automatique</li>
                <li>✅ Générer des PDF et Excel</li>
                <li>✅ Gérer vos clients et matières</li>
                <li>✅ Explorer toutes les fonctionnalités</li>
              </ul>
            </div>

            {/* Ce qu'on attend de vous */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-blue-700 mb-2 flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                Ce qu'on attend de vous
              </h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>💬 Signaler les bugs rencontrés</li>
                <li>💡 Suggérer des améliorations</li>
                <li>🧪 Tester sur différents navigateurs</li>
                <li>📊 Utiliser l'app dans vos cas réels</li>
                <li>⚡ Être réactif aux mises à jour</li>
              </ul>
            </div>
          </div>

          {/* Informations importantes */}
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  ⚠️ Version Bêta - À savoir
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Des bugs peuvent survenir - c'est normal !</li>
                    <li>Certaines fonctionnalités peuvent changer</li>
                    <li>Vos données de test ne sont pas garanties à 100%</li>
                    <li>Des mises à jour fréquentes sont à prévoir</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Comment nous contacter */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                const feedbackButton = document.querySelector('[aria-label="Donner mon avis"]') as HTMLButtonElement;
                if (feedbackButton) feedbackButton.click();
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              Donner mon avis
            </button>
            
            <div className="flex items-center text-sm text-gray-600">
              <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Utilisez le bouton bleu en bas à droite à tout moment
            </div>
          </div>
        </div>

        {/* Bouton fermer */}
        <button
          onClick={handleClose}
          className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Bouton pour ne plus afficher */}
      <div className="mt-4 pt-4 border-t border-blue-200">
        <button
          onClick={handleClose}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          ✓ J'ai compris, ne plus afficher ce message
        </button>
      </div>
    </div>
  );
};

export default BetaWelcome;
